import React, { useState, useRef } from 'react';
import {
    Paper,
    Typography,
    Box,
    Alert,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Chip,
    CircularProgress,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    FormHelperText,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
    Stack,
} from '@mui/material';
import {
    Security as SecurityIcon,
    Hub as HubIcon,
    Edit as EditIcon,
} from '@mui/icons-material';
import { useSiaCodesQuery, useUpdateSiaCodeMutation } from '../../api/JsdhNotifikaceService/service/SiaMutations';
import { useTypyUdalostiQuery, usePodtypyByTypIdQuery } from '../../api/JsdhNotifikaceService/service/AdminMutations';
import type { SiaCodeKonfig, SiaPriorita } from '../../api/JsdhNotifikaceService/generated/models/Sia';

const SIA_TAGS = ['objekt', 'panel', 'zona', 'umisteni', 'kod', 'nazev', 'obec', 'ulice', 'cislo'];
const SIA_PREVIEW_CTX: Record<string, string> = {
    objekt: 'Škola', panel: '1234', zona: 'Kotelna', umisteni: 'suterén',
    kod: 'FA', nazev: 'Požár', obec: 'Těškovice', ulice: 'Hlavní', cislo: '10',
};
const renderSiaPreview = (template: string): string => {
    if (!template) return '';
    let out = template;
    Object.entries(SIA_PREVIEW_CTX).forEach(([k, v]) => { out = out.split('{' + k + '}').join(v); });
    return out;
};

const SiaSettingsSection: React.FC = () => {
    const { data: codes, isLoading, error } = useSiaCodesQuery();

    const updateMutation = useUpdateSiaCodeMutation();
    const { data: typyUdalosti } = useTypyUdalostiQuery();
    const [editCode, setEditCode] = useState<SiaCodeKonfig | null>(null);
    const [form, setForm] = useState({
        nazev: '', priorita: 'MEDIUM' as SiaPriorita, vytvorPoplach: false, aktivni: true,
        typUdalostiId: null as number | null, podtypUdalostiId: null as number | null,
        popisSablona: '' as string, upresneniSablona: '' as string,
    });
    const { data: podtypy } = usePodtypyByTypIdQuery(form.typUdalostiId ?? undefined);
    // Kód bez zvoleného typu jede na výchozí EPS/EZS a podtyp neřeší.
    const chybiPodtyp = !!form.typUdalostiId && !form.podtypUdalostiId;
    const popisRef = useRef<HTMLInputElement | null>(null);
    const upresneniRef = useRef<HTMLInputElement | null>(null);

    const insertTag = (
        field: 'popisSablona' | 'upresneniSablona',
        ref: React.RefObject<HTMLInputElement | null>,
        tag: string,
    ) => {
        const el = ref.current;
        const current = form[field] || '';
        const pos = el?.selectionStart ?? current.length;
        setForm({ ...form, [field]: current.slice(0, pos) + `{${tag}}` + current.slice(pos) });
    };

    const openEdit = (c: SiaCodeKonfig) => {
        setEditCode(c);
        setForm({
            nazev: c.nazev, priorita: c.priorita, vytvorPoplach: c.vytvorPoplach, aktivni: c.aktivni,
            typUdalostiId: c.typUdalostiId ?? null, podtypUdalostiId: c.podtypUdalostiId ?? null,
            popisSablona: c.popisSablona ?? '', upresneniSablona: c.upresneniSablona ?? '',
        });
    };
    const handleSave = () => {
        if (!editCode) return;
        updateMutation.mutate({ id: editCode.id, data: {
            nazev: form.nazev, priorita: form.priorita, vytvorPoplach: form.vytvorPoplach, aktivni: form.aktivni,
            typUdalostiId: form.typUdalostiId, podtypUdalostiId: form.podtypUdalostiId,
            popisSablona: form.popisSablona || null, upresneniSablona: form.upresneniSablona || null,
        }}, { onSuccess: () => setEditCode(null) });
    };

    return (
        <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <SecurityIcon color="primary" />
                <Typography variant="h6">SIA EPS/EZS Nastaveni</Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Alert severity="info" sx={{ mb: 2 }}>
                SIA DC-09 protokol pro prijem udalosti z EPS/EZS panelu (Jablotron, DSC, Paradox, atd.).
                Panely se konfigurují pro jednotlivé monitorované objekty.
            </Alert>

            {/* Architektura */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Architektura
            </Typography>
            <Box sx={{
                bgcolor: 'grey.50',
                borderRadius: 1,
                p: 2,
                mb: 2,
                border: '1px solid',
                borderColor: 'grey.200'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">EPS/EZS Panel</Typography>
                        <Box sx={{
                            bgcolor: 'warning.light',
                            color: 'warning.contrastText',
                            px: 2, py: 1,
                            borderRadius: 1,
                            mt: 0.5
                        }}>
                            <Typography variant="body2" fontWeight="bold">Jablotron</Typography>
                        </Box>
                    </Box>
                    <Typography color="text.secondary">→</Typography>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">SIA Receiver</Typography>
                        <Box sx={{
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            px: 2, py: 1,
                            borderRadius: 1,
                            mt: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}>
                            <HubIcon fontSize="small" />
                            <Typography variant="body2" fontWeight="bold">MQTT Gateway</Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">UDP 5050 / TCP 5051</Typography>
                    </Box>
                    <Typography color="text.secondary">→</Typography>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Zpracovani</Typography>
                        <Box sx={{
                            bgcolor: 'success.main',
                            color: 'success.contrastText',
                            px: 2, py: 1,
                            borderRadius: 1,
                            mt: 0.5
                        }}>
                            <Typography variant="body2" fontWeight="bold">Backend</Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Table size="small" sx={{ mb: 2 }}>
                <TableBody>
                    <TableRow>
                        <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                            SIA Receiver
                        </TableCell>
                        <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip label="MQTT Gateway" color="primary" size="small" variant="outlined" />
                                <Typography variant="body2" color="text.secondary">
                                    (centralni prijem pro vsechny jednotky)
                                </Typography>
                            </Box>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                            UDP Port
                        </TableCell>
                        <TableCell>5050</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                            TCP Port
                        </TableCell>
                        <TableCell>5051</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                            Routing
                        </TableCell>
                        <TableCell>
                            <Typography variant="body2">
                                Podle Account ID (vygenerovaný kód ve formátu evidenční číslo-ID objektu-náhodný kód)
                            </Typography>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                            Podporovane sifrovani
                        </TableCell>
                        <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Chip label="Bez sifrovani" size="small" variant="outlined" />
                                <Chip label="AES-128" size="small" variant="outlined" />
                                <Chip label="AES-256" size="small" variant="outlined" />
                            </Box>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>

            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                Konfigurace SIA kodu
            </Typography>

            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                </Box>
            ) : error ? (
                <Alert severity="error">Chyba pri nacitani konfigurace SIA kodu</Alert>
            ) : (
                <Table size="small" sx={{ mb: 2 }}>
                    <TableBody>
                        {(codes ?? []).map(c => (
                            <TableRow key={c.id}>
                                <TableCell><strong>{c.siaCode}</strong> – {c.nazev}</TableCell>
                                <TableCell>{c.priorita}</TableCell>
                                <TableCell>{c.typUdalostiId ? 'číselník' : 'EPS/EZS (výchozí)'}</TableCell>
                                <TableCell align="right">
                                    <IconButton size="small" onClick={() => openEdit(c)}><EditIcon fontSize="small" /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            <Dialog open={!!editCode} onClose={() => setEditCode(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Upravit SIA kód {editCode?.siaCode}</DialogTitle>
                <DialogContent>
                    <TextField label="Název" fullWidth margin="dense"
                        value={form.nazev} onChange={e => setForm({ ...form, nazev: e.target.value })} />
                    <FormControl fullWidth margin="dense">
                        <InputLabel>Priorita</InputLabel>
                        <Select label="Priorita" value={form.priorita}
                            onChange={e => setForm({ ...form, priorita: e.target.value as SiaPriorita })}>
                            {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth margin="dense">
                        <InputLabel>Typ události</InputLabel>
                        <Select label="Typ události" value={form.typUdalostiId ?? ''}
                            onChange={e => setForm({ ...form, typUdalostiId: e.target.value ? Number(e.target.value) : null, podtypUdalostiId: null })}>
                            <MenuItem value=""><em>— výchozí EPS/EZS —</em></MenuItem>
                            {(typyUdalosti ?? []).map(t => <MenuItem key={t.id} value={t.id}>{t.nazev}</MenuItem>)}
                        </Select>
                    </FormControl>
                    {/* HO-135: podtyp je povinný, jakmile je zvolený typ. Bez něj
                        builder dosadí za podtyp název typu a poplach pak nese tentýž
                        text dvakrát — včetně hlasového hlášení. */}
                    <FormControl fullWidth margin="dense" required={!!form.typUdalostiId}
                        disabled={!form.typUdalostiId} error={chybiPodtyp}>
                        <InputLabel>Podtyp události</InputLabel>
                        <Select label="Podtyp události" value={form.podtypUdalostiId ?? ''}
                            onChange={e => setForm({ ...form, podtypUdalostiId: e.target.value ? Number(e.target.value) : null })}>
                            <MenuItem value=""><em>— žádný —</em></MenuItem>
                            {(podtypy ?? []).map(p => <MenuItem key={p.id} value={p.id}>{p.nazev}</MenuItem>)}
                        </Select>
                        {chybiPodtyp && (
                            <FormHelperText>U zvoleného typu vyberte i podtyp události.</FormHelperText>
                        )}
                    </FormControl>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                        {SIA_TAGS.map(t => (
                            <Chip key={`p-${t}`} label={`{${t}}`} size="small" variant="outlined"
                                onClick={() => insertTag('popisSablona', popisRef, t)} />
                        ))}
                    </Stack>
                    <TextField label="Šablona popisu" fullWidth margin="dense" multiline minRows={2}
                        inputRef={popisRef}
                        helperText={form.popisSablona ? `Náhled: ${renderSiaPreview(form.popisSablona)}` : 'Prázdné = výchozí auto-popis'}
                        value={form.popisSablona} onChange={e => setForm({ ...form, popisSablona: e.target.value })} />
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                        {SIA_TAGS.map(t => (
                            <Chip key={`u-${t}`} label={`{${t}}`} size="small" variant="outlined"
                                onClick={() => insertTag('upresneniSablona', upresneniRef, t)} />
                        ))}
                    </Stack>
                    <TextField label="Šablona upřesnění" fullWidth margin="dense" multiline minRows={2}
                        inputRef={upresneniRef}
                        helperText={form.upresneniSablona ? `Náhled: ${renderSiaPreview(form.upresneniSablona)}` : 'Prázdné = výchozí auto-upřesnění'}
                        value={form.upresneniSablona} onChange={e => setForm({ ...form, upresneniSablona: e.target.value })} />
                    <FormControlLabel control={<Switch checked={form.vytvorPoplach}
                        onChange={e => setForm({ ...form, vytvorPoplach: e.target.checked })} />} label="Vytvořit poplach" />
                    <FormControlLabel control={<Switch checked={form.aktivni}
                        onChange={e => setForm({ ...form, aktivni: e.target.checked })} />} label="Aktivní" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditCode(null)}>Zrušit</Button>
                    <Button variant="contained" onClick={handleSave}
                        disabled={updateMutation.isPending || chybiPodtyp}>Uložit</Button>
                </DialogActions>
            </Dialog>

            <Alert severity="warning" sx={{ mt: 2 }}>
                Pro pridani SIA panelu prejdete do detailu monitorovaneho objektu a otevrete tab "EPS/EZS".
                Do panelu zadejte přesně tu hodnotu Account ID, kterou tam uvidíte po vytvoření účtu
                (obsahuje evidenční číslo jednotky, ale není s ním totožná — je doplněná o ID objektu a náhodný kód).
            </Alert>
        </Paper>
    );
};

export default SiaSettingsSection;
